import { useRef, useState } from "react";
import { Upload, Sparkles, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import AIImageStudio from "./AIImageStudio";

export const inputCls = "w-full rounded-md border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-saffron-500/50";

export function Field({ label, children, required }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

export function TextF({ label, value, onChange, required, type = "text", placeholder }) {
  return (
    <Field label={label} required={required}>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={`f-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
    </Field>
  );
}

export function AreaF({ label, value, onChange, rows = 4, ai, contentType = "article" }) {
  return (
    <Field label={label}>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`f-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
      {ai && <AIButton text={value || ""} contentType={contentType}
        onApply={(txt, action) => onChange(action === "append" ? [value, txt].filter(Boolean).join("\n\n") : txt)} />}
    </Field>
  );
}

export function SelectF({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`f-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </Field>
  );
}

export function CheckF({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2.5 text-sm font-semibold text-navy">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-saffron-500" data-testid={`f-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
      {label}
    </label>
  );
}

export function ImageF({ label, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label}>
      <div className="flex flex-wrap items-center gap-3">
        {value && <img src={value} alt="" className="h-16 w-16 rounded-lg border border-gray-200 object-cover" />}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-saffron-500 disabled:opacity-50"
          data-testid={`upload-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {value ? "Change" : "Upload"}
        </button>
        <button type="button" onClick={() => setAiOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
          data-testid={`ai-image-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <ImagePlus size={15} /> AI Image
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-red-500">Remove</button>}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} />
      <AIImageStudio open={aiOpen} onClose={() => setAiOpen(false)} onUse={(media) => onChange(media.url)} />
    </Field>
  );
}

const AI_ACTIONS = [
  ["rewrite", "Rewrite"], ["improve", "Improve"], ["shorten", "Shorten"], ["expand", "Expand"],
  ["proofread", "Correct Grammar"], ["professional", "Make Professional"], ["translate_hindi", "Translate to Hindi"],
  ["translate_english", "Translate to English"], ["bilingual", "Make Bilingual"], ["seo", "Generate SEO"],
  ["summary", "Generate Summary"], ["social", "Generate Social Description"],
];

export const AI_MODELS = [
  ["gpt-5.4-mini", "ChatGPT 5.4 Mini (fast)"],
  ["gpt-5.6-terra", "ChatGPT 5.6 Terra"],
  ["gpt-5.6-luna", "ChatGPT 5.6 Luna"],
  ["gpt-5.6-sol", "ChatGPT 5.6 Sol"],
  ["gpt-5.5", "ChatGPT 5.5"],
  ["gpt-5.4", "ChatGPT 5.4"],
  ["gpt-5.2", "ChatGPT 5.2"],
  ["gpt-5.1", "ChatGPT 5.1"],
  ["gpt-5", "ChatGPT 5"],
  ["gpt-5-mini", "ChatGPT 5 Mini"],
  ["gpt-5-nano", "ChatGPT 5 Nano"],
  ["gpt-4.1", "ChatGPT 4.1"],
  ["gpt-4o", "ChatGPT 4o"],
  ["claude-haiku-4-5", "Claude Haiku 4.5 (fast)"],
  ["claude-sonnet-4-6", "Claude Sonnet 4.6"],
  ["claude-sonnet-5", "Claude Sonnet 5"],
  ["claude-opus-4-8", "Claude Opus 4.8"],
];

export function AIButton({ text, contentType, onApply, richActions = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [instruction, setInstruction] = useState("");
  const [lang, setLang] = useState("english");
  const [model, setModel] = useState("gpt-5.4-mini");
  const [lastMode, setLastMode] = useState("generate");
  const [imageOpen, setImageOpen] = useState(false);

  const run = async (mode) => {
    if (mode !== "generate" && mode !== "regenerate" && !text) {
      toast.error("Add some content first, then use an AI editing action");
      return;
    }
    if (mode === "generate" && !instruction.trim() && !text) {
      toast.error("Describe what the AI should write");
      return;
    }
    setBusy(true);
    setResult("");
    setLastMode(mode);
    try {
      const r = await api.post("/ai/assist", {
        mode, text, content_type: contentType, language: lang, model,
        prompt: instruction.trim() || text,
      });
      setResult(r.data.text);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const apply = (action = "replace") => {
    onApply(result, action);
    setOpen(false);
    toast.success("AI content inserted — review before publishing");
  };

  const applyImage = (media) => {
    const html = `<img src="${media.url}" alt="${(media.alt_text || media.title || "AI generated image").replace(/"/g, "&quot;")}" />`;
    if (richActions) onApply(html, "insert");
    else onApply(media.url, "append");
    toast.success("AI image inserted — review before publishing");
  };

  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(!open)} data-testid="ai-assist-toggle"
        className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-navy-800">
        <Sparkles size={13} /> AI Assistant
      </button>
      {open && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-navy-50/60 p-4" data-testid="ai-assist-panel">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Tell AI what you want to create or change</label>
          <textarea rows={4} value={instruction} onChange={(e) => setInstruction(e.target.value)}
            placeholder="Example: Write an article in English and Hindi about inclusive education for children with disabilities in Uttar Pradesh, approximately 800 words."
            className={inputCls} data-testid="ai-prompt" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select value={model} onChange={(e) => setModel(e.target.value)} className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-semibold" data-testid="ai-model">
              {AI_MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-semibold" data-testid="ai-lang">
              <option value="english">English</option><option value="hindi">हिन्दी</option><option value="bilingual">Bilingual</option>
            </select>
            <button type="button" onClick={() => run("generate")} disabled={busy || (!instruction.trim() && !text)} data-testid="ai-generate"
              className="rounded-full bg-saffron-500 px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-saffron-600 disabled:opacity-40">
              Generate
            </button>
            <button type="button" onClick={() => setImageOpen(true)} data-testid="ai-generate-image"
              className="rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-navy-800">
              Generate Image
            </button>
            {AI_ACTIONS.map(([mode, label]) => (
              <button key={mode} type="button" onClick={() => run(mode)} disabled={busy || !text} data-testid={`ai-${mode}`}
                className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-navy transition-colors hover:border-saffron-500 disabled:opacity-40">
                {label}
              </button>
            ))}
          </div>
          {busy && <p className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-testid="ai-generating"><Loader2 size={13} className="animate-spin" /> Generating…</p>}
          {result && (
            <div className="mt-3">
              <textarea rows={7} value={result} onChange={(e) => setResult(e.target.value)} className={inputCls} data-testid="ai-result" />
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => run(lastMode === "generate" ? "regenerate" : lastMode)} disabled={busy} className="rounded-md border border-gray-200 px-4 py-1.5 text-xs font-bold text-navy" data-testid="ai-regenerate">Regenerate</button>
                <button type="button" onClick={() => apply("replace")} className="btn-dark !px-4 !py-1.5 text-xs" data-testid="ai-accept">Accept</button>
                <button type="button" onClick={() => apply(richActions ? "insert" : "replace")} className="btn-dark !px-4 !py-1.5 text-xs" data-testid="ai-replace-selected">{richActions ? "Replace Selected Text" : "Replace Text"}</button>
                <button type="button" onClick={() => apply("append")} className="btn-dark !px-4 !py-1.5 text-xs" data-testid="ai-insert-below">Insert Below</button>
              </div>
            </div>
          )}
        </div>
      )}
      <AIImageStudio open={imageOpen} onClose={() => setImageOpen(false)} onUse={applyImage} entity={contentType} initialPrompt={instruction || text} />
    </div>
  );
}

export function StatusBadge({ value }) {
  const cls = { published: "bg-brandgreen-100 text-brandgreen", draft: "bg-saffron-100 text-saffron-600", scheduled: "bg-blue-100 text-blue-600", archived: "bg-gray-200 text-gray-600" }[value] || "bg-gray-100 text-gray-500";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${cls}`} data-testid={`status-${value}`}>{value}</span>;
}

export const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
